import { CSSProperties, useState, Fragment } from 'react'
import ReactMarkdown from 'react-markdown'
import { styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Icon from 'elements/Icon'
import TagSymphony from 'elements/TagSymphony'

interface AssistantMarkdownParserProps {
    /** The markdown content to render - now comes clean from backend */
    content: string
    /** Optional key for React rendering */
    keyPrefix?: string | number
}

// Function to parse content and handle tool call markers
const parseContentWithToolCalls = (content: string, keyPrefix?: string | number) => {
    // More robust regex pattern
    const toolCallRegex = /__TOOL_CALL__([\s\S]*?)__\/TOOL_CALL__/g
    const parts = []
    let lastIndex = 0
    let match

    while ((match = toolCallRegex.exec(content)) !== null) {
        
        // Add text before the tool call
        if (match.index > lastIndex) {
            const textBefore = content.slice(lastIndex, match.index)
            if (textBefore.trim()) {
                parts.push({
                    type: 'markdown',
                    content: textBefore,
                    key: `${keyPrefix}-text-${parts.length}`
                })
            }
        }

        // Add the tool call as a TagSymphony
        parts.push({
            type: 'tool_call',
            content: match[1].trim(), // The captured tool message, trimmed
            key: `${keyPrefix}-tool-${parts.length}`
        })

        lastIndex = toolCallRegex.lastIndex
    }

    // Add remaining text after last tool call
    if (lastIndex < content.length) {
        const remainingText = content.slice(lastIndex)
        if (remainingText.trim()) {
            parts.push({
                type: 'markdown',
                content: remainingText,
                key: `${keyPrefix}-text-${parts.length}`
            })
        }
    }

    // If no tool calls found, return the original content as markdown
    if (parts.length === 0) {
        parts.push({
            type: 'markdown',
            content: content,
            key: `${keyPrefix}-text-0`
        })
    }

    return parts
}

const AssistantMarkdownParser = ({ content, keyPrefix }: AssistantMarkdownParserProps) => {
    // Temporary debug to see what content we're receiving
    if (content.includes('TOOL_CALL')) {
        console.log('Content with tool calls:', JSON.stringify(content))
    }
    
    const contentParts = parseContentWithToolCalls(content, keyPrefix)

    return (
        <div className="markdown-content-container"
            style={{
                lineHeight: 1,
            }}>
            {contentParts.map((part) => {
                if (part.type === 'tool_call') {
                    return (
                        <div key={part.key} style={sty.toolCallContainer}>
                            <TagSymphony
                                className="toolCallTag"
                                color="purple"
                                icon="loader"
                                style={sty.toolCallTag}
                            >
                                {part.content}
                            </TagSymphony>
                        </div>
                    )
                } else {
                    return (
                        <ReactMarkdown
                            key={part.key}
                            components={{
                                p: ({ children }) => <div className="markdownParagraph" style={sty.markdownParagraph}>{children}</div>,
                                strong: ({ children }) => <strong className="markdownStrong" style={sty.markdownStrong}>{children}</strong>,
                                a: ({ href, children }) => {
                                    // Check if the link is an image URL
                                    const isImageUrl = /\.(png|jpg|jpeg|gif|webp|svg)(\?.*)?$/i.test(href || '');

                                    if (isImageUrl) {
                                        return (
                                            <ImageContainer href={href || ''} children={children} />
                                        );
                                    }

                                    // Regular link rendering
                                    return (
                                        <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="markdownLink"
                                            style={sty.markdownLink}
                                        >
                                            {children}
                                        </a>
                                    );
                                },
                                ol: ({ children }) => <ol className="markdownOrderedList" style={sty.markdownOrderedList}>{children}</ol>,
                                ul: ({ children }) => <ul className="markdownUnorderedList" style={sty.markdownUnorderedList}>{children}</ul>,
                                li: ({ children }) => <li className="markdownListItem" style={sty.markdownListItem}>{children}</li>,
                                h1: ({ children }) => <h1 className="markdownH1" style={sty.markdownH1}>{children}</h1>,
                                h2: ({ children }) => <h2 className="markdownH2" style={sty.markdownH2}>{children}</h2>,
                                h3: ({ children }) => <h3 className="markdownH3" style={sty.markdownH3}>{children}</h3>,
                                code: ({ children }) => <code className="markdownCode" style={sty.markdownCode}>{children}</code>,
                                blockquote: ({ children }) => <blockquote className="markdownBlockquote" style={sty.markdownBlockquote}>{children}</blockquote>,
                            }}
                        >
                            {part.content}
                        </ReactMarkdown>
                    )
                }
            })}
        </div>
    )
}

// Separate component for image handling with hover state
const ImageContainer = ({ href, children }: { href: string; children: React.ReactNode }) => {
    const [isHovered, setIsHovered] = useState(false)

    return (
        <div
            className="generatedImageContainer"
            style={sty.generatedImageContainer}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <img
                src={href}
                alt="Generated image"
                style={sty.generatedImage}
                onError={(e) => {
                    // Fallback to link if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.nextElementSibling?.setAttribute('style', 'display: block');
                }}
            />
            <div
                className="generatedImageActions"
                style={{
                    ...sty.generatedImageActions,
                    opacity: isHovered ? 1 : 0,
                }}
            >
                <button
                    className="imageActionButton"
                    style={sty.imageActionButton}
                    onClick={() => window.open(href, '_blank')}
                    type="button"
                >
                    <Icon className="viewImageIcon" icon="expand" size={16} />
                    View Full Size
                </button>
                <button
                    className="imageActionButton"
                    style={sty.imageActionButton}
                    onClick={() => {
                        const link = document.createElement('a');
                        link.href = href;
                        link.download = `generated-image-${Date.now()}.png`;
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }}
                    type="button"
                >
                    <Icon className="downloadImageIcon" icon="download" size={16} />
                    Download
                </button>
            </div>
            {/* Fallback link if image fails to load */}
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="markdownLink"
                style={styx(sty.markdownLink, { display: 'none' })}
            >
                {children || href}
            </a>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    markdownParagraph: {
        margin: '0 0',
        lineHeight: 1.5,
    },
    markdownStrong: {
        fontWeight: 600,
        color: Colors.white,
    },
    markdownLink: {
        color: Colors.purple,
        textDecoration: 'none',
        fontWeight: 500,
    },
    markdownOrderedList: {
        paddingLeft: 20,
        margin: '0 0 2px 0',
        listStyleType: 'decimal',
        lineHeight: 1
    },
    markdownUnorderedList: {
        paddingLeft: 20,
        margin: '0 0 2px 0',
        listStyleType: 'disc',
        lineHeight: 1
    },
    markdownListItem: {
        margin: '0',
        lineHeight: 1.2,
        marginBottom: 0,
        listStyleType: 'disc',
    },
    markdownH1: {
        fontSize: 24,
        fontWeight: 700,
        margin: '16px 0 8px 0',
        color: Colors.white,
    },
    markdownH2: {
        fontSize: 20,
        fontWeight: 600,
        margin: '12px 0 6px 0',
        color: Colors.white,
    },
    markdownH3: {
        fontSize: 18,
        fontWeight: 600,
        margin: '10px 0 4px 0',
        color: Colors.white,
    },
    markdownCode: {
        backgroundColor: Colors.greyLight,
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 14,
        fontFamily: 'monospace',
    },
    markdownBlockquote: {
        borderLeft: `4px solid ${Colors.purple}`,
        paddingLeft: 12,
        margin: '8px 0',
        fontStyle: 'italic',
        color: Colors.greyDark,
    },
    generatedImageContainer: {
        margin: '12px 0',
        borderRadius: 8,
        overflow: 'hidden',
        maxWidth: '100%',
        position: 'relative',
        display: 'inline-block',
        backgroundColor: Colors.greyLight,
    },
    generatedImage: {
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
    },
    generatedImageActions: {
        position: 'absolute',
        bottom: 8,
        left: 8,
        right: 8,
        display: 'flex',
        gap: 8,
        opacity: 0,
        transition: 'opacity 0.2s ease',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderRadius: 6,
        padding: 8,
    },
    imageActionButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        border: 'none',
        borderRadius: 4,
        padding: '6px 12px',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 500,
        color: Colors.white,
        transition: 'all 0.2s ease',
        flex: 1,
        justifyContent: 'center',
    },
    toolCallContainer: {
        margin: '8px 0',
        display: 'flex',
        justifyContent: 'flex-start',
    },
    toolCallTag: {
        // Additional styles can be added here if needed
    },
}

export default AssistantMarkdownParser 